import { useCallback, useEffect, useState } from "react";
import { resolveCategoryMeta } from "./categoryDirectoryConfig";

const DEFAULT_API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

async function readCategoryList(url, signal) {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal,
  });

  const data = await response.json();

  if (
    !response.ok ||
    data?.success !== true ||
    !Array.isArray(data.categories)
  ) {
    throw new Error(
      `Unable to load categories (${response.status}).`
    );
  }

  return data.categories;
}

function makeOptions(allowedValues, managedRecords) {
  if (
    allowedValues.some(
      (value) => typeof value !== "string" || !value.trim()
    )
  ) {
    throw new Error(
      "The Business API returned invalid category values."
    );
  }

  const metadata = new Map();

  managedRecords.forEach((item) => {
    if (
      item?.active === true &&
      typeof item.value === "string"
    ) {
      metadata.set(item.value, item);
    }
  });

  return [...new Set(allowedValues)]
    .map((value) => {
      const saved = metadata.get(value);
      const fallback = resolveCategoryMeta(value);

      const specialLabel =
        value === "others"
          ? "Other business (specify)"
          : value === "multiple"
          ? "Multiple (specify)"
          : "";

      return {
        // Preserve the exact value accepted by the Business API.
        value,
        name:
          specialLabel ||
          saved?.name?.trim() ||
          fallback.label ||
          value,
        slug: saved?.slug || fallback.slug,
        icon: fallback.icon || "",
        isSpecial: Boolean(specialLabel),
      };
    })
    .sort(
      (a, b) =>
        Number(a.isSpecial) - Number(b.isSpecial) ||
        a.name.localeCompare(
          b.name,
          undefined,
          { sensitivity: "base" }
        )
    );
}

export default function useManagedCategories(
  apiBase = DEFAULT_API_BASE
) {
  const base = String(
    apiBase || DEFAULT_API_BASE
  ).replace(/\/+$/, "");

  const [revision, setRevision] = useState(0);

  const [state, setState] = useState({
    categories: [],
    loading: true,
    loaded: false,
    error: "",
    warning: "",
  });

  const reload = useCallback(
    () => setRevision((number) => number + 1),
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let timedOut = false;

    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10000);

    setState((current) => ({
      ...current,
      loading: true,
      error: "",
      warning: "",
    }));

    async function load() {
      try {
        const [allowed, details] =
          await Promise.allSettled([
            readCategoryList(
              `${base}/api/categories`,
              controller.signal
            ),
            readCategoryList(
              `${base}/api/config/categories`,
              controller.signal
            ),
          ]);

        if (disposed) {
          return;
        }

        if (allowed.status !== "fulfilled") {
          throw allowed.reason;
        }

        const haveDetails =
          details.status === "fulfilled";

        const categories = makeOptions(
          allowed.value,
          haveDetails ? details.value : []
        );

        setState({
          categories,
          loading: false,
          loaded: true,
          error: "",
          warning: haveDetails
            ? ""
            : "Custom labels unavailable; using saved labels.",
        });
      } catch {
        if (!disposed) {
          setState((current) => ({
            ...current,
            loading: false,
            error: timedOut
              ? "Category request timed out. Try Refresh types."
              : "Unable to load business types. Try Refresh types.",
          }));
        }
      } finally {
        clearTimeout(timer);
      }
    }

    load();

    return () => {
      disposed = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [base, revision]);

  return {
    ...state,
    reload,
  };
}